import type {
  InState,
  OutState,
  PropsAnimatorOptions,
} from "../../components/PropsAnimator/PropsAnimator";
import type { RibbonControlProps } from "../../Controls/RibbonControlProps";
import { getLayoutKeyForControlGroup } from "../RibbonBottomBar/addLayoutCacheKeyToRibbonState";
import type {
  Layout,
  RibbonControlGroupMLRProps,
} from "../RibbonControlGroupMLR";
import {
  layoutTransitionEnterDuration,
  layoutTransitionExitDuration,
} from "../RibbonControlGroupMLR/RibbonControlGroupMLR.classNames";

export interface LayoutChanges {
  /** If true, the control is in a new position in the layout */
  moving: { [controlId: string]: boolean };
}

type ControlsAndLayout = { controls: RibbonControlProps[]; layout?: Layout };

function diffLayouts(
  before: ControlsAndLayout,
  after: ControlsAndLayout
): LayoutChanges {
  const layoutChanges: LayoutChanges = { moving: {} };
  const beforeItems = processLayout(before.layout, before.controls);
  const afterItems = processLayout(after.layout, after.controls);

  for (const id in beforeItems.idToRow) {
    if (beforeItems.idToRow[id] !== afterItems.idToRow[id]) {
      layoutChanges.moving[id] = true; // Being removed or moved
    } else if (beforeItems.idIsStacked[id] !== afterItems.idIsStacked[id]) {
      layoutChanges.moving[id] = true; // From unstacked <-> stacked
    } else if (beforeItems.idToType[id] !== afterItems.idToType[id]) {
      layoutChanges.moving[id] = true; // Changed prop types (AppGallery -> FlyoutAnchor)
    }

    delete afterItems.idToRow[id];
  }

  for (const addedItems in afterItems.idToRow) {
    layoutChanges.moving[addedItems] = true;
  }

  return layoutChanges;
}

interface ProcessedLayout {
  idToRow: { [id: string]: number };
  idIsStacked: { [id: string]: boolean };
  idToType: { [id: string]: string };
}

function processLayout(
  layout: Layout | undefined,
  controls: RibbonControlProps[]
): ProcessedLayout {
  const result: ProcessedLayout = {
    idToRow: {},
    idIsStacked: {},
    idToType: {},
  };
  for (const control of controls) {
    result.idToType[control.id] = control.type;
    if (!layout) {
      result.idToRow[control.id] = 1;
    }
  }

  if (!layout) {
    return result;
  }

  for (const section of layout.sections) {
    if (!Array.isArray(section)) {
      continue; // It's a section divider
    }

    for (let rowIndex = 0; rowIndex < section.length; rowIndex += 1) {
      const row = section[rowIndex];
      if (!Array.isArray(row)) {
        continue;
      }

      for (const rowItem of row) {
        if (typeof rowItem === "string") {
          result.idToRow[rowItem] = rowIndex;
          continue;
        }

        if (Array.isArray(rowItem)) {
          for (const stackedId of rowItem) {
            if (stackedId) {
              result.idToRow[stackedId] = rowIndex;
              result.idIsStacked[stackedId] = true;
            }
          }
        }
      }
    }
  }

  return result;
}

interface TransitionInState {
  layout?: Layout;
  controls: RibbonControlProps[];
  layoutCacheKey: string;
}

interface DataFromHiddenRender {
  finalWidth: number;
}

export type ControlGroupAnimationProps = {
  initialWidth?: number;
  finalWidth?: number;
  layoutChanges?: LayoutChanges;
};

export type ControlGroupWithAnimatingLayout = RibbonControlGroupMLRProps &
  ControlGroupAnimationProps;

const layoutWrapperSelector = ".layoutWrapper";
const transition = {
  className: "controlGroupLayout",
  inDuration: layoutTransitionEnterDuration,
  outDuration: layoutTransitionExitDuration,
};

/**
 * Exit: Render the old controls and layout, and fade them out
 * Enter: Transition to the new layout size, then fade in the controls
 */
export function getControlGroupLayoutAnimationOptions<
  TProps extends ControlGroupWithAnimatingLayout,
>() {
  const options: PropsAnimatorOptions<
    TProps,
    ControlGroupAnimationProps,
    TransitionInState,
    DataFromHiddenRender
  > = {
    shouldAnimate,
    getInitialInState,
    getDataFromHiddenRender,
    getInState,
    getCacheKeyForHiddenRender,
    getOutState,
    shouldAnimateOut,
    transition,
  };

  return options;
}

function shouldAnimate<TProps extends ControlGroupWithAnimatingLayout>(
  nextProps: TProps
) {
  return !!nextProps.animate;
}

function getInitialInState<TProps extends ControlGroupWithAnimatingLayout>(
  nextProps: TProps
) {
  return {
    state: {
      controls: nextProps.controls,
      layout: nextProps.layout,
      layoutCacheKey: getLayoutKeyForControlGroup({
        type: "Multiline",
        ...nextProps,
      }),
    },
  };
}

/**
 * Provide the width of the old layout as the inital width and the width of the new layout as the final width to animate smoothly
 */
function getInState<TProps extends ControlGroupWithAnimatingLayout>(
  props: TProps,
  previousOutState: OutState<TProps>,
  visibleWrapper: HTMLElement,
  valueFromHiddenRender: DataFromHiddenRender
): InState<ControlGroupAnimationProps, TransitionInState> {
  const layoutWrapper = visibleWrapper.querySelector(layoutWrapperSelector);
  return {
    state: {
      controls: props.controls,
      layout: props.layout,
      layoutCacheKey: getLayoutKeyForControlGroup({
        type: "Multiline",
        ...props,
      }),
    },
    animationProps: {
      initialWidth: layoutWrapper!.clientWidth,
      finalWidth: valueFromHiddenRender.finalWidth,
      layoutChanges: previousOutState.offsetExitedProps.layoutChanges,
    },
  };
}

function shouldAnimateOut<TProps extends RibbonControlGroupMLRProps>(
  nextProps: TProps,
  inState: InState<ControlGroupAnimationProps, TransitionInState>
): boolean {
  return (
    getLayoutKeyForControlGroup({ type: "Multiline", ...nextProps }) !==
    inState.state!.layoutCacheKey
  );
}

function getCacheKeyForHiddenRender<TProps extends RibbonControlGroupMLRProps>(
  props: TProps
) {
  return getLayoutKeyForControlGroup({ type: "Multiline", ...props });
}

/** Measure the width of the new layout so we can use it as the final width for the new props */
function getDataFromHiddenRender(hiddenWrapper: HTMLElement) {
  const layoutWrapper = hiddenWrapper.querySelector(layoutWrapperSelector);
  return {
    finalWidth: (layoutWrapper && layoutWrapper.clientWidth) || 0,
  };
}

/**
 * Get the state needed to Exit the previous props. We exit by fading out the old controls and layout
 */
function getOutState<TProps extends ControlGroupWithAnimatingLayout>(
  nextProps: TProps,
  previousInState: InState<ControlGroupAnimationProps, TransitionInState>
): OutState<TProps> {
  return {
    offsetExitedProps: {
      layout: previousInState.state!.layout,
      controls: previousInState.state!.controls,
      layoutChanges: diffLayouts(previousInState.state!, nextProps),
    } as Partial<TProps>,
    hiddenPropOverrides: {
      animate: false,
    } as Partial<TProps>,
  };
}
